<template>
  <section class="vp-card" data-profile-card="company">
    <!--
      Always open. This card is the only one left in the profile rail, so collapsing it
      bought nothing and the accordion it belonged to would slam it shut whenever another
      card elsewhere opened.
    -->
    <div class="vp-card-head">
      <h6 class="vp-card-title">
        <i class="ph-duotone ph-buildings"></i>{{ t('profile.company.locationTitle') }}
      </h6>
    </div>
    <div class="vp-card-body">
      <div v-if="isLoading" class="vp-loading">
        <span class="spinner-border spinner-border-sm text-primary" role="status">
          <span class="visually-hidden">{{ t('common.loading') }}</span>
        </span>
      </div>
      <template v-else>
        <div class="vp-kv company-contact">
          <div class="vp-kv-item">
            <div class="vp-kv-label">{{ t('common.email') }}</div>
            <p class="vp-kv-value">{{ profile.email || t('common.notSpecified') }}</p>
          </div>
          <div class="vp-kv-item">
            <div class="vp-kv-label">{{ t('common.phone') }}</div>
            <p class="vp-kv-value">{{ profile.phone || t('common.notSpecified') }}</p>
          </div>
        </div>

        <!-- Manual Address Input with Autocomplete -->
        <div class="vp-form-field company-address position-relative">
          <label class="vp-label" for="company-address-input">{{ t('profile.company.address') }}</label>
          <div class="company-address-field">
            <i class="ph-duotone ph-magnifying-glass company-address-icon"></i>
            <input
              id="company-address-input"
              v-model="address"
              type="text"
              class="vp-control company-address-input"
              :placeholder="t('profile.map.addressPlaceholder')"
              @input="onAddressInput"
              @blur="onAddressBlur"
              @keyup.enter.exact.prevent="searchAddressOnMap"
              @keydown.down.prevent="selectNextSuggestion"
              @keydown.up.prevent="selectPreviousSuggestion"
              @keydown.enter.prevent="selectCurrentSuggestion"
              :disabled="!canEditTenantInfo"
            />
          </div>
          <!-- Address Suggestions Dropdown -->
          <div v-if="addressSuggestions.length > 0 && showSuggestions" class="address-suggestions">
            <div
              v-for="(suggestion, index) in addressSuggestions"
              :key="index"
              class="address-suggestion-item"
              :class="{ 'active': selectedSuggestionIndex === index }"
              @click="selectSuggestion(suggestion)"
              @mouseenter="selectedSuggestionIndex = index"
            >
              <i class="ph-duotone ph-map-pin"></i>
              <span>{{ suggestion.place_name }}</span>
            </div>
          </div>
          <div class="vp-help">{{ t('profile.map.addressHint') }}</div>
        </div>

        <!-- Map -->
        <div class="map-instructions">
          <i class="ph-duotone ph-hand-pointing"></i>
          <span>{{ t('profile.map.instructions') }}</span>
        </div>
        <div id="map-container"></div>

        <!-- Pin dropped confirmation -->
        <div v-if="isPinDragged" class="pin-dropped-message">
          <i class="ph-duotone ph-check-circle"></i>
          <span>{{ t('profile.map.pinUpdated') }}</span>
        </div>

        <!-- Save button - only show if address changed -->
        <div v-if="addressChanged && canEditTenantInfo" class="company-actions">
          <button
            type="button"
            class="vp-btn vp-btn-primary vp-btn-sm"
            :disabled="isSaving"
            @click="saveAddress"
          >
            <span v-if="isSaving" class="spinner-border spinner-border-sm"></span>
            {{ isSaving ? t('profile.map.saving') : t('profile.map.saveAddress') }}
          </button>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { onMounted, onUnmounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useMapboxMap } from '@/composables/useMapboxMap';
import { useAddressAutocomplete } from '@/composables/useAddressAutocomplete';
import { useGeocoding } from '@/composables/useGeocoding';
import { useAddressManagement } from '@/composables/useAddressManagement';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
  name: 'ProfileCompanyCard',
  props: {
    profile: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    targetTenantId: {
      type: String,
      default: ''
    }
  },
  emits: ['update:address'],
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    // No accordion membership: the card never collapses, so a deep link from the
    // profile-completion checklist only has to scroll to it. Profile.vue finds it by
    // its data-profile-card attribute, which stays.
    
    // Initialize Mapbox token
    const mapboxToken = (() => {
      const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
      if (!token) {
        console.warn('Mapbox access token not found. Please set VITE_MAPBOX_ACCESS_TOKEN in your .env file');
      } else {
        mapboxgl.accessToken = token;
      }
      return token;
    })();
    
    // Permission check
    const canEditTenantInfo = computed(() => hasPermission('Supplier.TenantInfo.Set') && !props.isReadOnly);
    const currentUser = computed(() => store.getters['auth/currentUser']);
    
    // Use composables
    const profileRef = computed(() => props.profile);
    const effectiveTenantId = computed(() => props.targetTenantId || currentUser.value?.tenantId || null);
    const addressManagement = useAddressManagement(profileRef, emit, effectiveTenantId);
    const geocoding = useGeocoding(mapboxToken);
    const autocomplete = useAddressAutocomplete(mapboxToken);
    const mapManagement = useMapboxMap(
      mapboxToken,
      canEditTenantInfo,
      {
        lat: props.profile.latitude || 41.0082,
        lng: props.profile.longitude || 28.9784
      }
    );
    
    // Helper: Create marker drag end handler
    const createMarkerDragHandler = () => {
      return async (lat, lng) => {
        addressManagement.updateLocation(lat, lng);
        const result = await geocoding.reverseGeocode(lat, lng);
        if (result) {
          addressManagement.updateAddress(result.address);
          addressManagement.updateCountryCode(result.countryCode);
          addressManagement.updateCountryName(result.countryName);
        }
      };
    };
    
    // Helper: Initialize map
    const initMap = () => {
      mapManagement.initMap('map-container', createMarkerDragHandler());
    };
    
    // Helper: Update map location from address management
    const syncMapLocation = () => {
      const { lat, lng } = addressManagement.currentLocation.value;
      if (lat && lng) {
        mapManagement.currentLocation.value = { lat, lng };
        mapManagement.updateMapLocation(lat, lng, 13);
      }
    };
    
    // Helper: fill in whichever half of the stored address is missing.
    // A stored pin without a text gets its text from the pin. A stored text without a pin
    // (a registration address typed without picking a suggestion) gets its pin from the
    // text. With neither there is nothing to derive: the default map centre is not the
    // company's address, and turning it into one showed a made-up street.
    const completeStoredAddress = () => {
      const { lat, lng } = addressManagement.currentLocation.value;
      const hasPin = addressManagement.hasStoredLocation.value;
      const text = addressManagement.address.value?.trim();

      if (hasPin && !text) {
        geocoding.reverseGeocode(lat, lng).then(result => {
          if (result) {
            addressManagement.updateAddress(result.address);
            addressManagement.updateCountryCode(result.countryCode);
            addressManagement.updateCountryName(result.countryName);
          }
        });
      } else if (!hasPin && text) {
        geocoding.forwardGeocode(text).then(result => {
          if (result) {
            addressManagement.updateLocation(result.lat, result.lng);
            mapManagement.updateMapLocation(result.lat, result.lng);
          }
        });
      }
    };
    
    // Helper: Handle successful geocode result
    const handleGeocodeResult = (result) => {
      if (!result) {
        store.dispatch('notify', {
          message: t('profile.map.addressNotFound'),
          type: 'warning'
        });
        return;
      }
      
      addressManagement.updateLocation(result.lat, result.lng);
      addressManagement.updateAddress(result.address);
      addressManagement.updateCountryCode(result.countryCode);
      addressManagement.updateCountryName(result.countryName);
      mapManagement.updateMapLocation(result.lat, result.lng);
    };
    
    // Search address on map
    const searchAddressOnMap = async () => {
      if (!addressManagement.address.value.trim() || !mapboxToken) {
        return;
      }
      
      const result = await geocoding.forwardGeocode(addressManagement.address.value);
      handleGeocodeResult(result);
    };
    
    // Handle address input
    const handleAddressInput = () => {
      autocomplete.onAddressInput(addressManagement.address.value);
    };
    
    // Select suggestion
    const selectSuggestion = (suggestion) => {
      addressManagement.updateAddress(suggestion.place_name);
      addressManagement.updateCountryCode(geocoding.extractCountryCode(suggestion));
      addressManagement.updateCountryName(geocoding.extractCountryName(suggestion));
      autocomplete.clearSuggestions();
      
      const [lng, lat] = suggestion.center;
      addressManagement.updateLocation(lat, lng);
      mapManagement.updateMapLocation(lat, lng);
    };
    
    // Select current suggestion (keyboard)
    const selectCurrentSuggestion = () => {
      const index = autocomplete.selectedSuggestionIndex.value;
      const suggestions = autocomplete.addressSuggestions.value;
      
      if (index >= 0 && suggestions[index]) {
        selectSuggestion(suggestions[index]);
      } else {
        searchAddressOnMap();
      }
    };
    
    // Save address
    const saveAddress = async () => {
      await addressManagement.saveAddress(() => {
        const { lat, lng } = addressManagement.currentLocation.value;
        mapManagement.updateMapLocation(lat, lng);
      });
    };
    
    // Helper: Validate mapbox token
    const validateMapboxToken = () => {
      if (!mapboxToken) {
        store.dispatch('notify', {
          message: t('profile.map.tokenMissing'),
          type: 'error'
        });
        return false;
      }
      return true;
    };
    
    // Helper: Initialize map after address load
    const initializeMapAfterLoad = () => {
      syncMapLocation();
      initMap();
      syncMapLocation();
      completeStoredAddress();
    };
    
    // Load address and initialize map
    const initializeComponent = async () => {
      if (!validateMapboxToken()) {
        return;
      }
      
      await addressManagement.loadAddressFromApi();
      setTimeout(initializeMapAfterLoad, 100);
    };
    
    // Helper: Handle profile address change
    const handleProfileAddressChange = (newProfile) => {
      addressManagement.updateAddress(newProfile.address || '');
      addressManagement.resetOriginalAddress();
    };
    
    // Helper: Handle profile location change
    const handleProfileLocationChange = (newProfile) => {
      const lat = newProfile.latitude || 41.0082;
      const lng = newProfile.longitude || 28.9784;
      addressManagement.updateLocation(lat, lng);
      mapManagement.updateMapLocation(lat, lng, 13);
    };
    
    // Watch profile changes
    watch(() => props.profile, (newProfile, oldProfile) => {
      if (newProfile.address !== oldProfile?.address) {
        handleProfileAddressChange(newProfile);
      }
      
      if (newProfile.latitude !== oldProfile?.latitude || 
          newProfile.longitude !== oldProfile?.longitude) {
        handleProfileLocationChange(newProfile);
      }
    }, { deep: true });
    
    // Lifecycle hooks
    onMounted(() => {
      initializeComponent();
    });
    
    onUnmounted(() => {
      mapManagement.cleanup();
      autocomplete.cleanup();
    });
    
    return {
      address: addressManagement.address,
      isSaving: addressManagement.isSaving,
      isPinDragged: mapManagement.isPinDragged,
      addressChanged: addressManagement.addressChanged,
      searchAddressOnMap,
      onAddressChange: () => {},
      onAddressInput: handleAddressInput,
      onAddressBlur: autocomplete.onAddressBlur,
      saveAddress,
      currentUser,
      t,
      canEditTenantInfo,
      addressSuggestions: autocomplete.addressSuggestions,
      showSuggestions: autocomplete.showSuggestions,
      selectedSuggestionIndex: autocomplete.selectedSuggestionIndex,
      selectSuggestion,
      selectNextSuggestion: autocomplete.selectNextSuggestion,
      selectPreviousSuggestion: autocomplete.selectPreviousSuggestion,
      selectCurrentSuggestion
    };
  }
};
</script>

<style scoped>
.company-contact {
  margin-bottom: 14px;
}

.company-address {
  margin-bottom: 14px;
}

.company-address-field {
  position: relative;
  display: flex;
  align-items: center;
}

.company-address-icon {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  color: var(--vp-gray-500);
  pointer-events: none;
}

.company-address-input {
  padding-left: 32px;
}

.map-instructions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 6px 12px;
  font-size: 0.75rem;
  color: var(--vp-gray-600);
  background: var(--vp-gray-100);
  border-radius: var(--vp-radius);
}

/* Mapbox map container styling */
#map-container {
  width: 100%;
  height: 250px;
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  overflow: hidden;
  z-index: 0;
}

.pin-dropped-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--vp-success);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.company-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

/* Custom marker styling */
.custom-marker {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  transform: translate(-50%, -100%);
}

.custom-marker i {
  text-shadow: 0 0 2px white;
}

/* Address Suggestions Dropdown */
.address-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  max-height: 300px;
  margin-top: 2px;
  overflow-y: auto;
  background: var(--vp-surface);
  border: 1px solid var(--vp-gray-300);
  border-radius: var(--vp-radius);
  box-shadow: var(--vp-shadow-card);
}

.address-suggestion-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  color: var(--vp-body-color);
  cursor: pointer;
  border-bottom: 1px solid var(--vp-gray-200);
  transition: background-color 0.15s ease;
}

.address-suggestion-item:last-child {
  border-bottom: none;
}

.address-suggestion-item:hover,
.address-suggestion-item.active {
  background-color: rgba(var(--vp-primary-rgb), 0.06);
  color: var(--vp-primary);
}

.address-suggestion-item i {
  flex-shrink: 0;
  font-size: 0.95rem;
  color: var(--vp-gray-500);
}

.address-suggestion-item:hover i,
.address-suggestion-item.active i {
  color: var(--vp-primary);
}

/* Mapbox controls styling */
:deep(.mapboxgl-ctrl-group) {
  background: var(--vp-surface);
  border-radius: var(--vp-radius-chip);
  box-shadow: var(--vp-shadow-card);
}

:deep(.mapboxgl-ctrl-zoom-in),
:deep(.mapboxgl-ctrl-zoom-out) {
  width: 30px;
  height: 30px;
  border: none;
  background: var(--vp-surface);
  color: var(--vp-body-color);
}

:deep(.mapboxgl-ctrl-zoom-in):hover,
:deep(.mapboxgl-ctrl-zoom-out):hover {
  background: var(--vp-gray-100);
}
</style>
